import { FormikHelpers, FormikProps } from 'formik'
import { ViewMode } from 'types'
import getLocalized from 'i18n'
import { useRef, useState, useCallback } from 'react'
import { useConfirmation } from './useConfirmation'
import { useHandleEscapeKey } from './useHandleEscapeKey'

const localized = getLocalized()

const optimiseSubmitFormData = (data: any) => {
  Object.keys(data).forEach((key) => {
    // trim forename and surname
    if (key === 'forename' || key === 'surname') {
      data[key] = data[key].trim()
    }

    // other parsing could go here too
  })
  return data
}

export const useViewSwitchingForm = <FormValues>({
  getChanges,
  onSubmitForm,
  onLeaveForm,
  onCancelEditing,
  onEnterEditing,
  initialView = 'view',
}: {
  getChanges: ({
    initialValues,
    values,
  }: {
    initialValues: FormValues
    values: FormValues
  }) => Partial<FormValues>
  onSubmitForm: (updates: Partial<FormValues>) => void
  onLeaveForm: () => void
  onCancelEditing?: (form: FormikProps<FormValues>) => void
  onEnterEditing?: () => void
  initialView?: ViewMode
}) => {
  const formikRef = useRef<FormikProps<FormValues>>(null)
  const [viewMode, setViewMode] = useState<ViewMode>(initialView)
  const viewModeRef = useRef<ViewMode>(viewMode)
  viewModeRef.current = viewMode
  const hasFormChanged = useCallback(() => {
    const form = formikRef.current
    if (form) {
      const values = form.values
      const initialValues = form.initialValues
      const changed = getChanges({ initialValues, values })
      return !!Object.keys(changed).length
    } else {
      return false
    }
  }, [formikRef, getChanges])

  const confirmCancel = useConfirmation({
    header: localized.confirmationModal.closeWithoutSaving.header,
    body: localized.confirmationModal.closeWithoutSaving.body,
    confirmText: localized.confirmationModal.closeWithoutSaving.confirmText,
    shouldConfirm: hasFormChanged,
  })
  const handleEnterEditing = useCallback(() => {
    setViewMode('edit')
    onEnterEditing && onEnterEditing()
  }, [setViewMode, onEnterEditing])

  const handleSubmitForm = useCallback(
    (data: Partial<FormValues>) => {
      setViewMode('view')
      optimiseSubmitFormData(data)
      onSubmitForm(data)
    },
    [onSubmitForm, setViewMode]
  )

  const handleCancelEditing = useCallback(async (): Promise<boolean> => {
    const form = formikRef.current
    if (form) {
      const view = viewModeRef.current
      if (view === 'edit' && !(await confirmCancel())) {
        return false
      }
      onCancelEditing && onCancelEditing(form)
      form.handleReset()
      setViewMode('view')
    }
    return true
  }, [formikRef, viewModeRef, confirmCancel, onCancelEditing])

  const handleCancel = useCallback(() => {
    if (viewModeRef.current === 'edit') {
      handleCancelEditing()
    } else if (viewModeRef.current === 'view') {
      onLeaveForm()
    }
  }, [handleCancelEditing, onLeaveForm, viewModeRef])

  useHandleEscapeKey(handleCancel)

  const handleSubmit = useCallback(
    (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      const view = viewModeRef.current
      if (view === 'edit') {
        handleSubmitForm(values)
      } else if (viewModeRef.current === 'view') {
        handleEnterEditing()
        setSubmitting(false)
      }
    },
    [handleSubmitForm, handleEnterEditing]
  )

  return {
    formikRef,
    viewMode,
    handleEnterEditing,
    handleCancelEditing,
    handleSubmitForm,
    handleLeaveForm: onLeaveForm,
    hasFormChanged,
    handleSubmit,
    handleCancel,
  }
}
